import { t } from "../i18n/t";
import type { ModelCatalog } from "../catalog/schema";

export type LayoutRefs = {
  root: HTMLElement;
  header: HTMLElement;
  partsPane: HTMLElement;
  viewerPane: HTMLElement;
  canvas: HTMLCanvasElement;
  loading: HTMLElement;
  controls: HTMLElement;
  explodeToggle: HTMLButtonElement;
  explodeSlider: HTMLInputElement;
  explodePct: HTMLElement;
  tooltip: HTMLElement;
  toast: HTMLElement;
};

const SVG_PHONE = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13 1.05.37 2.07.7 3.05a2 2 0 0 1-.45 2.11L8.09 10.91a16 16 0 0 0 6 6l2.03-2.27a2 2 0 0 1 2.11-.45c.98.34 2 .57 3.05.7A2 2 0 0 1 22 16.92Z"/></svg>`;

export function buildLayout(
  mountId: string,
  catalog: ModelCatalog,
  embedded: boolean,
): LayoutRefs {
  const root = document.getElementById(mountId);
  if (!root) throw new Error(`Mount node #${mountId} not found`);

  // Header (hidden when embedded into kayserbetten.eu — host header takes over)
  const headerHtml = embedded
    ? ""
    : `
    <header class="kb-header" role="banner">
      <div class="kb-logo">
        <span class="kb-logo-mark" aria-hidden="true">
          <svg viewBox="0 0 260 260" preserveAspectRatio="xMidYMid meet" focusable="false">
            <g transform="translate(0,260) scale(0.1,-0.1)" fill="currentColor">
              <path d="M1209 2210 c-146 -76 -157 -288 -18 -376 21 -13 39 -30 40 -37 4 -41 -9 -134 -27 -190 -11 -37 -54 -128 -67 -142 -7 -8 -24 -29 -39 -48 -27 -34 -148 -125 -148 -111 0 4 -8 0 -17 -8 -20 -17 -132 -50 -144 -43 -4 3 -11 0 -15 -6 -4 -8 -9 -7 -15 2 -6 10 -9 10 -9 2 0 -7 -6 -10 -12 -8 -7 2 -37 7 -66 10 -93 9 -244 79 -221 102 26 26 32 133 10 188 -25 66 -115 135 -163 126 -5 0 -8 4 -8 10 0 5 -4 8 -9 4 -5 -3 -22 -7 -38 -9 -48 -6 -83 -18 -83 -28 0 -5 -4 -7 -9 -4 -9 6 -80 -68 -82 -85 -2 -12 -5 -21 -11 -39 -13 -32 -16 -60 -7 -65 4 -3 6 -12 3 -20 -7 -18 33 -104 61 -128 46 -40 107 -59 173 -53 56 5 62 3 62 -14 0 -10 5 -22 10 -25 6 -3 10 -15 10 -26 0 -10 4 -19 8 -19 5 0 9 -6 10 -12 1 -13 20 -67 43 -123 7 -16 13 -32 14 -35 1 -3 5 -14 10 -25 8 -19 126 -320 147 -375 5 -14 9 -26 9 -28 -1 -1 6 -16 14 -32 8 -16 16 -33 16 -37 1 -9 12 -37 31 -80 6 -16 12 -32 12 -37 2 -14 46 -40 46 -27 0 8 6 9 18 2 11 -6 24 -6 35 0 11 7 17 7 17 0 0 -7 8 -7 26 -1 14 6 23 7 21 2 -3 -4 10 -7 29 -7 19 0 34 4 34 8 0 4 8 4 18 0 21 -10 43 -10 59 0 6 4 15 3 18 -3 5 -8 11 -8 20 0 10 8 15 8 20 0 5 -8 10 -8 20 0 10 8 15 8 20 0 5 -8 10 -8 20 0 10 8 15 8 20 0 5 -8 10 -8 20 0 10 8 15 8 20 0 5 -8 10 -8 20 0 9 8 15 8 20 0 3 -6 12 -7 18 -3 18 11 36 12 42 2 3 -4 16 -4 28 0 13 5 30 5 39 0 11 -6 19 -5 23 1 4 6 11 7 18 3 16 -10 57 -10 57 0 0 4 8 3 18 -2 13 -7 22 -7 29 0 8 8 13 7 17 0 5 -8 13 -8 27 -1 13 8 19 7 19 0 0 -8 6 -8 20 0 11 6 20 7 20 3 0 -4 11 -8 25 -8 14 0 25 4 25 8 0 4 9 3 19 -3 14 -7 22 -7 26 0 4 6 11 7 18 3 16 -10 57 -10 57 0 0 4 9 3 19 -3 14 -7 22 -7 26 0 5 7 11 8 19 1 7 -6 17 -6 26 -1 9 6 17 5 21 -2 6 -10 41 43 38 57 0 3 8 29 20 58 11 29 26 66 32 82 7 17 12 33 11 37 -1 5 3 8 10 8 6 0 9 3 6 6 -4 3 1 22 9 42 31 73 36 87 39 97 6 24 195 499 200 504 2 3 35 6 72 7 86 2 94 4 140 39 49 37 77 94 79 164 1 64 -16 114 -55 154 -28 29 -84 60 -108 60 -8 0 -14 4 -14 8 0 5 -6 7 -12 4 -7 -2 -29 -6 -48 -8 -79 -10 -145 -61 -172 -132 -20 -51 -17 -131 5 -173 17 -31 17 -33 0 -39 -10 -4 -20 -10 -23 -13 -3 -4 -34 -19 -70 -33 -93 -37 -222 -49 -280 -27 -3 1 -9 3 -15 5 -5 1 -12 3 -15 4 -3 2 -12 4 -20 6 -27 5 -109 46 -121 61 -6 8 -16 14 -21 14 -30 0 -168 180 -168 218 0 6 -5 23 -12 39 -30 73 -38 190 -15 208 85 63 112 97 122 154 3 19 8 38 11 43 3 4 1 8 -5 8 -5 0 -8 9 -6 20 10 50 -52 137 -120 173 -42 21 -142 19 -186 -3z"/>
            </g>
          </svg>
        </span>
        <span>
          KayserBetten
          <small>${t("app.title")}</small>
        </span>
      </div>
      <span class="kb-header-divider"></span>
      <div class="kb-header-title">
        <strong>${escapeHtml(catalog.name.de)}</strong>
        Wählen Sie das gesuchte Bauteil
      </div>
      <div class="kb-header-spacer"></div>
      <div class="kb-header-meta">
        <a href="tel:${t("header.phone").replace(/\s/g, "")}" rel="tel">${SVG_PHONE}<span>${t("header.phone")}</span></a>
      </div>
      <span class="kb-lang">${t("header.lang")}</span>
    </header>
  `;

  root.innerHTML = `
    ${headerHtml}
    <div class="kb-body">
      <aside class="kb-parts-pane" role="complementary" aria-label="Bauteile"></aside>
      <section class="kb-viewer-pane" role="main">
        <div class="kb-decor-half tl" aria-hidden="true"></div>
        <div class="kb-decor-ring br" aria-hidden="true"></div>

        <canvas class="kb-canvas" id="kb-canvas"></canvas>

        <div class="kb-viewer-hint">
          ${t("viewer.hint.line1")}<br>
          ${t("viewer.hint.line2")}
        </div>

        <div class="kb-controls">
          <button class="kb-explode-toggle" aria-pressed="false">
            <span class="kb-toggle-dot"></span>
            <span>${t("viewer.explosion")}</span>
          </button>
          <div class="kb-controls-slider-wrap">
            <input class="kb-slider" type="range" min="0" max="100" value="0" />
            <span class="kb-slider-pct">0 %</span>
          </div>
        </div>

        <div class="kb-tooltip"></div>

        <div class="kb-loading">
          <div class="kb-spinner"></div>
          <div class="kb-loading-label">${t("viewer.loading")}</div>
        </div>
      </section>
    </div>
    <div class="kb-toast" role="status" aria-live="polite"></div>
  `;

  return {
    root,
    header: root.querySelector(".kb-header") as HTMLElement,
    partsPane: root.querySelector(".kb-parts-pane") as HTMLElement,
    viewerPane: root.querySelector(".kb-viewer-pane") as HTMLElement,
    canvas: root.querySelector("#kb-canvas") as HTMLCanvasElement,
    loading: root.querySelector(".kb-loading") as HTMLElement,
    controls: root.querySelector(".kb-controls") as HTMLElement,
    explodeToggle: root.querySelector(".kb-explode-toggle") as HTMLButtonElement,
    explodeSlider: root.querySelector(".kb-slider") as HTMLInputElement,
    explodePct: root.querySelector(".kb-slider-pct") as HTMLElement,
    tooltip: root.querySelector(".kb-tooltip") as HTMLElement,
    toast: root.querySelector(".kb-toast") as HTMLElement,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
